/**
 * The sign-in step's behavior half: Telegram's sign-in on the studio's bot, the
 * development test-account sign-in, and the welcome back for a visitor who left
 * to get Telegram (design D7: the address keeps the pick; nothing is held while
 * they install). Draws nothing.
 */
import { useEffect, useMemo, useState } from 'react';

import { useTelegramSignIn } from '../../api/session';
import { useSiteStudio } from '../../api/site';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { contract, type SignInStepActions, type SignInStepEvents, type SignInStepState } from './contract';

export type SignInStepRuntime = PieceRuntime<typeof contract, SignInStepState, SignInStepActions>;

const STORE_LINKS = {
  appStore: 'https://apps.apple.com/app/telegram-messenger/id686449807',
  googlePlay: 'https://play.google.com/store/apps/details?id=org.telegram.messenger',
} as const;

export function useSignInStep(input: PieceInput<SignInStepEvents> = {}): SignInStepRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const studioName = useSiteStudio().data?.name ?? '';
  const telegram = useTelegramSignIn();
  const [away, setAway] = useState(false);
  const [welcomeBack, setWelcomeBack] = useState(false);

  // The store opens in a new tab; this page greets the visitor when they return to it.
  useEffect(() => {
    if (!away) return;
    const back = () => {
      if (document.visibilityState === 'visible') {
        setAway(false);
        setWelcomeBack(true);
      }
    };
    window.addEventListener('focus', back);
    document.addEventListener('visibilitychange', back);
    return () => {
      window.removeEventListener('focus', back);
      document.removeEventListener('visibilitychange', back);
    };
  }, [away]);

  const actions = useMemo<SignInStepActions>(
    () => ({
      signIn: () => {
        emit('signInPressed', {});
        telegram.signIn();
      },
      signInAsTestAccount: telegram.signInAsTestAccount,
      leftToGetTelegram: () => setAway(true),
    }),
    [emit, telegram],
  );

  return {
    state: {
      studioName,
      ready: telegram.ready,
      attempt: telegram.state,
      testAccount: Boolean(telegram.signInAsTestAccount),
      storeLinks: STORE_LINKS,
      welcomeBack,
    },
    actions,
    options,
  };
}
